// TODO: i18n — see Auth.js for pattern, replace hardcoded strings with t('namespace.key')
const C = {
  bg: '#F2EDE4', card: '#FDFAF6', border: '#DDD4C6',
  amber: '#B8721F', amberLight: '#D4902A', brown: '#2C1D14',
  muted: '#9A7B68', success: '#2E6B4A', danger: '#B03020',
  successBg: '#EBF5EF', dangerBg: '#FDECEA', warningBg: '#FBF3E8',
}

const S = {
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(44,29,20,0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 2000, padding: '20px', fontFamily: 'Georgia, serif',
  },
  modal: {
    background: C.card, borderRadius: '16px', width: '100%',
    maxWidth: '500px', maxHeight: '88vh', overflowY: 'auto',
    boxShadow: '0 8px 40px rgba(44,29,20,0.2)', border: `1px solid ${C.border}`,
  },
  header: {
    padding: '24px 28px 0', display: 'flex',
    justifyContent: 'space-between', alignItems: 'center',
  },
  title: { fontSize: '20px', fontWeight: '700', color: C.brown },
  closeBtn: {
    background: 'none', border: 'none', fontSize: '22px',
    cursor: 'pointer', color: C.muted, padding: '0', lineHeight: 1,
  },
  body: { padding: '20px 28px 28px' },
  scoreHero: {
    display: 'flex', alignItems: 'center', gap: '20px',
    padding: '20px', background: C.bg, borderRadius: '12px',
    border: `1px solid ${C.border}`, marginBottom: '24px',
  },
  divider: { borderTop: `1px solid ${C.border}`, margin: '20px 0' },
  sectionTitle: {
    fontSize: '11px', color: C.muted, letterSpacing: '2px',
    textTransform: 'uppercase', marginBottom: '12px',
    fontFamily: 'Calibri, sans-serif',
  },
  factorRow: {
    display: 'flex', alignItems: 'center', gap: '12px',
    marginBottom: '14px',
  },
  factorBar: {
    flex: 1, height: '6px', borderRadius: '3px',
    background: C.border, overflow: 'hidden',
  },
  factorBarFill: (pct, color) => ({
    height: '100%', borderRadius: '3px',
    width: `${pct}%`, background: color,
    transition: 'width 0.8s ease',
  }),
  factorScore: {
    fontSize: '13px', fontWeight: '700', color: C.brown,
    fontFamily: 'Calibri, sans-serif', width: '40px', textAlign: 'right', flexShrink: 0,
  },
  factorLabel: {
    fontSize: '13px', color: C.brown, fontFamily: 'Calibri, sans-serif',
    width: '160px', flexShrink: 0,
  },
  tipCard: {
    background: C.warningBg, border: `1px solid ${C.amber}33`,
    borderRadius: '10px', padding: '12px 14px', marginBottom: '10px',
    display: 'flex', alignItems: 'flex-start', gap: '10px',
  },
  tipIcon: { fontSize: '16px', flexShrink: 0, marginTop: '1px' },
  tipText: {
    fontSize: '13px', color: C.brown, fontFamily: 'Calibri, sans-serif',
    lineHeight: 1.5, flex: 1,
  },
  tipPoints: {
    fontSize: '12px', fontWeight: '700', color: C.amber,
    fontFamily: 'Calibri, sans-serif', flexShrink: 0, marginTop: '1px',
  },
  perfectCard: {
    background: C.successBg, border: `1px solid ${C.success}33`,
    borderRadius: '10px', padding: '14px 16px', marginBottom: '10px',
    display: 'flex', alignItems: 'center', gap: '10px',
  },
  closeFullBtn: {
    width: '100%', background: `linear-gradient(135deg, ${C.amber}, ${C.amberLight})`,
    border: 'none', borderRadius: '10px', padding: '13px',
    color: C.card, fontSize: '14px', fontWeight: '700',
    letterSpacing: '2px', textTransform: 'uppercase',
    cursor: 'pointer', fontFamily: 'Georgia, serif', marginTop: '20px',
  },
}

function getLetterGrade(score) {
  if (score >= 93) return 'A+'
  if (score >= 90) return 'A'
  if (score >= 87) return 'A-'
  if (score >= 83) return 'B+'
  if (score >= 80) return 'B'
  if (score >= 77) return 'B-'
  if (score >= 73) return 'C+'
  if (score >= 70) return 'C'
  if (score >= 67) return 'C-'
  if (score >= 63) return 'D+'
  if (score >= 60) return 'D'
  return 'F'
}

function getScoreColor(score) {
  if (score >= 80) return { primary: C.success, light: '#EBF5EF' }
  if (score >= 60) return { primary: C.amber, light: '#FBF3E8' }
  return { primary: C.danger, light: '#FDECEA' }
}

export default function ScoreModal({ score, profile, appliances, systems, onClose }) {
  const grade = getLetterGrade(score)
  const scoreColor = getScoreColor(score)

  // Calculate individual factor scores
  const profileFields = ['address', 'city', 'state', 'zip', 'property_type', 'year_built', 'bedrooms', 'bathrooms', 'sqft']
  const filledProfile = profileFields.filter(f => profile?.[f]).length
  const profileScore = Math.round((filledProfile / profileFields.length) * 25)

  const totalItems = (appliances?.length || 0) + (systems?.length || 0)
  const coverageScore = Math.min(25, Math.round((totalItems / 5) * 25))

  const totalAppliances = appliances?.length || 0
  let maintenanceScore = 0
  if (totalAppliances > 0) {
    const goodCount = appliances.filter(a => a.status === 'good').length
    maintenanceScore = Math.round((goodCount / totalAppliances) * 50)
  } else if (profile?.address) {
    maintenanceScore = 15
  }

  // Build improvement tips
  const tips = []

  if (profileScore < 25) {
    const missing = profileFields.filter(f => !profile?.[f]).length
    const gain = 25 - profileScore
    tips.push({
      icon: '🏠',
      text: `Complete your home profile — ${missing} field${missing !== 1 ? 's' : ''} missing. Add year built, bedrooms, bathrooms, and square footage.`,
      points: `+${gain} pts`,
      action: 'settings',
    })
  }

  if (coverageScore < 25) {
    const needed = Math.max(0, 5 - totalItems)
    const gain = 25 - coverageScore
    if (needed > 0) {
      tips.push({
        icon: '🍽️',
        text: `Add ${needed} more appliance${needed !== 1 ? 's' : ''} or system${needed !== 1 ? 's' : ''} to reach full coverage score.`,
        points: `+${gain} pts`,
      })
    }
  }

  if (maintenanceScore < 50 && totalAppliances === 0) {
    tips.push({
      icon: '🔧',
      text: 'Add your appliances to unlock the full maintenance score — worth up to 50 points.',
      points: '+50 pts',
    })
  }

  const factors = [
    {
      label: 'Home Profile',
      score: profileScore,
      max: 25,
      color: profileScore >= 20 ? C.success : profileScore >= 12 ? C.amber : C.danger,
      description: `${filledProfile} of ${profileFields.length} profile fields completed`,
    },
    {
      label: 'Coverage',
      score: coverageScore,
      max: 25,
      color: coverageScore >= 20 ? C.success : coverageScore >= 12 ? C.amber : C.danger,
      description: `${totalItems} appliance${totalItems !== 1 ? 's' : ''} & system${totalItems !== 1 ? 's' : ''} tracked (5 for full score)`,
    },
    {
      label: 'Maintenance',
      score: maintenanceScore,
      max: 50,
      color: maintenanceScore >= 40 ? C.success : maintenanceScore >= 25 ? C.amber : C.danger,
      description: totalAppliances > 0
        ? `${appliances.filter(a => a.status === 'good').length} of ${totalAppliances} appliances up to date`
        : 'Add appliances to unlock this score',
    },
  ]

  return (
    <div style={S.overlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={S.modal}>
        <div style={S.header}>
          <div style={S.title}>Home Health Score</div>
          <button style={S.closeBtn} onClick={onClose}>×</button>
        </div>

        <div style={S.body}>
          {/* Score hero */}
          <div style={S.scoreHero}>
            {/* Mini ring */}
            <div style={{ position: 'relative', width: '80px', height: '80px', flexShrink: 0 }}>
              <svg width="80" height="80" viewBox="0 0 80 80">
                <circle cx="40" cy="40" r="34" fill="none" stroke={C.border} strokeWidth="7" />
                <circle
                  cx="40" cy="40" r="34"
                  fill="none" stroke={scoreColor.primary} strokeWidth="7"
                  strokeLinecap="round"
                  strokeDasharray={`${(score / 100) * 214} 214`}
                  transform="rotate(-90 40 40)"
                />
              </svg>
              <div style={{
                position: 'absolute', inset: 0,
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center',
              }}>
                <div style={{ fontSize: '20px', fontWeight: '700', color: scoreColor.primary, lineHeight: 1 }}>{score}</div>
                <div style={{ fontSize: '13px', fontWeight: '700', color: scoreColor.primary }}>{grade}</div>
              </div>
            </div>

            <div>
              <div style={{ fontSize: '18px', fontWeight: '700', color: C.brown, marginBottom: '4px' }}>
                {grade.startsWith('A') ? 'Excellent' : grade.startsWith('B') ? 'Good' : grade.startsWith('C') ? 'Fair' : 'Needs Work'}
              </div>
              <div style={{ fontSize: '13px', color: C.muted, fontFamily: 'Calibri, sans-serif', lineHeight: 1.6 }}>
                {score >= 90
                  ? 'Your home is exceptionally well maintained. Keep it up.'
                  : score >= 75
                  ? 'Your home is in good shape with a few areas to improve.'
                  : score >= 60
                  ? 'Some maintenance attention will go a long way.'
                  : 'Your home needs some TLC — small steps add up fast.'}
              </div>
            </div>
          </div>

          {/* Score breakdown */}
          <div style={S.sectionTitle}>Score Breakdown</div>
          {factors.map((factor, i) => (
            <div key={i} style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                <div style={{ fontSize: '13px', fontWeight: '600', color: C.brown, fontFamily: 'Calibri, sans-serif' }}>
                  {factor.label}
                </div>
                <div style={{ fontSize: '13px', fontWeight: '700', color: factor.color, fontFamily: 'Calibri, sans-serif' }}>
                  {factor.score} / {factor.max}
                </div>
              </div>
              <div style={S.factorRow}>
                <div style={S.factorBar}>
                  <div style={S.factorBarFill((factor.score / factor.max) * 100, factor.color)} />
                </div>
              </div>
              <div style={{ fontSize: '11px', color: C.muted, fontFamily: 'Calibri, sans-serif', marginTop: '-4px' }}>
                {factor.description}
              </div>
            </div>
          ))}

          <div style={S.divider} />

          {/* Improvement tips */}
          <div style={S.sectionTitle}>
            {tips.length === 0 ? 'Your Score' : 'How to Improve Your Score'}
          </div>

          {tips.length === 0 ? (
            <div style={S.perfectCard}>
              <span style={{ fontSize: '20px' }}>🏆</span>
              <div style={{ fontSize: '14px', color: C.success, fontFamily: 'Calibri, sans-serif', fontWeight: '600' }}>
                You're maxing out your current score. As Casavio adds date tracking and task completion, your score will become even more precise.
              </div>
            </div>
          ) : (
            tips.map((tip, i) => (
              <div key={i} style={S.tipCard}>
                <span style={S.tipIcon}>{tip.icon}</span>
                <div style={S.tipText}>{tip.text}</div>
                <div style={S.tipPoints}>{tip.points}</div>
              </div>
            ))
          )}

          <div style={{
            fontSize: '12px', color: C.muted, fontFamily: 'Calibri, sans-serif',
            lineHeight: 1.6, marginTop: '16px', fontStyle: 'italic',
          }}>
            Your score will become more precise over time as Casavio adds task completion tracking, service history, and date-based maintenance currency.
          </div>

          <button style={S.closeFullBtn} onClick={onClose}>Got It</button>
        </div>
      </div>
    </div>
  )
}